import React from "react";
import imageManifest from "../../../static/portfolio/generated/runtime-image-manifest.json";
import imagePreviews from "../../../static/portfolio/generated/image-previews.json";

const portfolioPrefix = "/static/portfolio/";
const rockyPortfolioPrefix = "/Rocky/static/portfolio/";

export const getSiteBasePath = () => {
  if (typeof window === "undefined") return "";
  const explicitBase = window.__ROCKY_ASSET_BASE__;
  if (typeof explicitBase === "string") return explicitBase.replace(/\/$/, "");
  return window.location.pathname === "/Rocky" || window.location.pathname.startsWith("/Rocky/") ? "/Rocky" : "";
};

export const resolveAssetUrl = (url) => {
  if (!url || /^(data:|blob:|https?:\/\/)/.test(url)) return url || "";
  if (url.startsWith("/Rocky/")) return url;
  if (url.startsWith("/static/")) return `${getSiteBasePath()}${url}`;
  return url;
};

export const getPortfolioKey = (src = "") => {
  const cleanSrc = String(src).split("?")[0].split("#")[0];
  if (cleanSrc.startsWith(rockyPortfolioPrefix)) return cleanSrc.slice(rockyPortfolioPrefix.length);
  if (cleanSrc.startsWith(portfolioPrefix)) return cleanSrc.slice(portfolioPrefix.length);
  if (cleanSrc.startsWith("portfolio/")) return cleanSrc.slice("portfolio/".length);
  return cleanSrc.replace(/^\/+/, "");
};

export const getImageEntry = (src) => {
  const key = getPortfolioKey(src);
  return imageManifest.images?.[key] || null;
};

export const getImagePreview = (src) => imagePreviews[getPortfolioKey(src)] || "";

export const getImageRequest = (src, { sizes = "100vw", maxWidth = 1920, skipAvif = false } = {}) => {
  const entry = getImageEntry(src);
  if (!entry) return { src: resolveAssetUrl(src) };
  return {
    src: getVariantImage(src, { formats: ["jpeg"], maxWidth })?.src || resolveAssetUrl(src),
    srcset: buildSrcSet(entry, "jpeg", maxWidth),
    sizes,
    sources: orderImageSources([
      !skipAvif && { type: "image/avif", srcset: buildSrcSet(entry, "avif", maxWidth) },
      { type: "image/webp", srcset: buildSrcSet(entry, "webp", maxWidth) },
    ].filter((source) => source && source.srcset), entry, maxWidth),
  };
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const pickVariants = (entry, format, maxWidth) => {
  const variants = entry?.[format] || [];
  if (!maxWidth) return variants;
  const filtered = variants.filter((variant) => variant.width <= maxWidth);
  return filtered.length ? filtered : variants.slice(0, 1);
};

const orderImageSources = (sources, entry, maxWidth) => {
  const bytes = (format) => pickVariants(entry, format, maxWidth).reduce((sum, variant) => sum + (variant.bytes || 0), 0);
  // Use the lighter of the existing high-quality encodings, never re-encode or
  // lower the full-resolution artwork to save transfer bytes.
  if (bytes("webp") < bytes("avif")) return [...sources].reverse();
  return sources;
};

export const getVariantImage = (imageOrSrc, { formats = ["webp", "jpeg", "avif"], maxWidth } = {}) => {
  const src = typeof imageOrSrc === "string" ? imageOrSrc : imageOrSrc?.src || imageOrSrc?.image || "";
  const entry = getImageEntry(src);
  if (!entry) return null;
  for (const format of formats) {
    const variants = pickVariants(entry, format, maxWidth);
    const variant = variants[variants.length - 1];
    if (variant) {
      return {
        src: resolveAssetUrl(variant.src),
        width: variant.width,
        height: variant.height,
        entry,
      };
    }
  }
  return null;
};

export const buildSrcSet = (entry, format, maxWidth) =>
  pickVariants(entry, format, maxWidth)
    .map((variant) => `${resolveAssetUrl(variant.src)} ${variant.width}w`)
    .join(", ");

export const getStageImage = (imageOrSrc) => {
  const src = typeof imageOrSrc === "string" ? imageOrSrc : imageOrSrc?.src || imageOrSrc?.image || "";
  const entry = getImageEntry(src);
  const stage = entry?.stage;
  return {
    src: resolveAssetUrl(stage?.src || src),
    width: stage?.width || entry?.width,
    height: stage?.height || entry?.height,
    entry,
  };
};

export const getFullImage = (imageOrSrc) => {
  const src = typeof imageOrSrc === "string" ? imageOrSrc : imageOrSrc?.src || imageOrSrc?.image || "";
  const entry = getImageEntry(src);
  return {
    src: resolveAssetUrl(entry?.fallback?.src || src),
    width: entry?.fallback?.width || entry?.width,
    height: entry?.fallback?.height || entry?.height,
    entry,
  };
};

export const OptimizedImage = ({
  src,
  alt = "",
  className = "",
  loading = "lazy",
  decoding = "async",
  draggable = false,
  fetchPriority,
  sizes = "100vw",
  maxWidth,
  useStageFallback = false,
  skipAvif = false,
  deferred = false,
}) => {
  const entry = getImageEntry(src);
  const fallback = useStageFallback ? getStageImage(src) : getFullImage(src);
  const preview = getImagePreview(src);
  const managed = deferred || Boolean(preview);
  const imgProps = {
    src: fallback.src,
    alt,
    loading,
    decoding,
    draggable,
  };

  if (className) imgProps.className = className;
  if (fallback.width) imgProps.width = fallback.width;
  if (fallback.height) imgProps.height = fallback.height;
  if (sizes) imgProps.sizes = sizes;
  if (fetchPriority) imgProps.fetchpriority = fetchPriority;

  if (managed) {
    imgProps["data-scene-src"] = imgProps.src;
    if (preview) {
      imgProps.src = preview;
      imgProps.style = { aspectRatio: `${fallback.width} / ${fallback.height}` };
    }
    else delete imgProps.src;
  }
  if (!entry) return <img {...imgProps} />;

  const avifSrcSet = skipAvif ? "" : buildSrcSet(entry, "avif", maxWidth);
  const webpSrcSet = buildSrcSet(entry, "webp", maxWidth);
  const jpegSrcSet = buildSrcSet(entry, "jpeg", maxWidth);
  if (jpegSrcSet) imgProps[managed ? "data-scene-srcset" : "srcSet"] = jpegSrcSet;
  const sources = orderImageSources([
    avifSrcSet && { type: "image/avif", srcset: avifSrcSet },
    webpSrcSet && { type: "image/webp", srcset: webpSrcSet },
  ].filter(Boolean), entry, maxWidth);

  return (
    <picture>
      {sources.map((source) => <source key={source.type} type={source.type} {...{ [managed ? "data-scene-srcset" : "srcSet"]: source.srcset }} sizes={sizes} />)}
      <img {...imgProps} />
    </picture>
  );
};

const attrsToHtml = (attrs) =>
  Object.entries(attrs)
    .filter(([, value]) => value !== undefined && value !== null && value !== false && value !== "")
    .map(([key, value]) => (value === true ? key : `${key}="${escapeHtml(value)}"`))
    .join(" ");

export const createOptimizedImageHTML = (src, alt = "", options = {}) => {
  const {
    className,
    loading = "lazy",
    decoding = "async",
    draggable = "false",
    fetchPriority,
    sizes = "100vw",
    maxWidth,
    useStageFallback = false,
    skipAvif = false,
    fallbackMaxWidth,
    fallbackFormats,
    pictureAttributes = {},
    preview = false,
  } = options;
  const entry = getImageEntry(src);
  const variantFallback = fallbackMaxWidth
    ? getVariantImage(src, { formats: fallbackFormats || ["webp", "jpeg", "avif"], maxWidth: fallbackMaxWidth })
    : null;
  const fallback = variantFallback || (useStageFallback ? getStageImage(src) : getFullImage(src));
  const imgAttrs = {
    class: className,
    src: fallback.src,
    alt,
    loading,
    decoding,
    draggable,
    width: fallback.width,
    height: fallback.height,
    sizes,
    fetchpriority: fetchPriority,
  };

  if (preview && getImagePreview(src)) {
    imgAttrs.src = getImagePreview(src);
    imgAttrs.loading = "eager";
    imgAttrs.style = `aspect-ratio:${fallback.width} / ${fallback.height}`;
    return `<picture ${attrsToHtml(pictureAttributes)}><img ${attrsToHtml(imgAttrs)} /></picture>`;
  }

  if (!entry) return `<img ${attrsToHtml(imgAttrs)} />`;

  const avifSrcSet = skipAvif ? "" : buildSrcSet(entry, "avif", maxWidth);
  const webpSrcSet = buildSrcSet(entry, "webp", maxWidth);
  const jpegSrcSet = buildSrcSet(entry, "jpeg", maxWidth);
  if (jpegSrcSet) imgAttrs.srcset = jpegSrcSet;

  const sources = [
    avifSrcSet ? `<source type="image/avif" srcset="${escapeHtml(avifSrcSet)}" sizes="${escapeHtml(sizes)}" />` : "",
    webpSrcSet ? `<source type="image/webp" srcset="${escapeHtml(webpSrcSet)}" sizes="${escapeHtml(sizes)}" />` : "",
  ].join("");

  const pictureAttrs = attrsToHtml(pictureAttributes);
  return `<picture${pictureAttrs ? ` ${pictureAttrs}` : ""}>${sources}<img ${attrsToHtml(imgAttrs)} /></picture>`;
};

